import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  adminApi,
  type CliType,
  type DeviceListItem,
  type DeviceProjectMapEntry,
  type MountPoint,
  type PlacementType,
} from '../api/admin';
import { Modal } from '../components/modal';

// 「デバイスマウント」メニュー。各デバイスに、どのマウントポイント（論理名）を載せるかを管理する。
// 同じデータ（DeviceProjectMapEntry = 1件が「デバイス × マウントポイント」のペア）を、
//   - デバイスから見る：デバイスごとに、そのデバイスに載っているマウントを見る
//   - マウントポイントから見る：同じマウントポイントを使うデバイス＋役割(purpose)を横断で見る
// の2タブで切り替える。視点が変わるだけで裏のデータは1つ。編集はどちらのタブからでも同じ実体に効く。
// 物理パスは MountPoint 側に持ち、ここでは MountPoint を参照するだけ（登録/変更は MountPoint を選ぶ）。

const cliOptions: { value: CliType; label: string }[] = [
  { value: 'claude_code', label: 'Claude Code' },
  { value: 'gemini_cli', label: 'Gemini CLI' },
  { value: 'cursor_agent', label: 'Cursor Agent' },
];
const cliLabel = (v: CliType) => cliOptions.find((o) => o.value === v)?.label ?? v;

const placementOptions: { value: PlacementType; label: string }[] = [
  { value: 'local_pc', label: 'PC(強力)' },
  { value: 'codespaces', label: 'codespace' },
  { value: 'vps_backup', label: 'vps' },
  { value: 'peer_pc', label: '軽量PC' },
];
const placementLabel = (v: PlacementType) =>
  placementOptions.find((o) => o.value === v)?.label ?? v;


// DIR の用途/役割バッジ（緑系）。assign 画面・デバイス画面と見た目を揃える。
function PurposeBadge({ name }: { name: string }) {
  return (
    <span
      style={{
        padding: '1px 7px',
        borderRadius: 999,
        background: '#dcfce7',
        color: '#166534',
        fontSize: 11,
      }}
    >
      {name}
    </span>
  );
}

const deviceName = (d: DeviceListItem) => d.nickname || d.name;

type TabKey = 'device' | 'path';

// マウント追加/変更ダイアログの対象。editing があれば変更、なければ追加。
// fixedDevice: 追加先デバイスを固定（デバイスから見るタブからの追加）。
// presetMountPointId: マウントポイントから見るタブで「このマウントポイントを別デバイスに追加」する際の初期値。
type PathTarget = {
  editing: DeviceProjectMapEntry | null;
  fixedDevice: DeviceListItem | null;
  presetMountPointId?: number;
};

export function PathsPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['devices'] });

  const devicesQuery = useQuery({
    queryKey: ['devices'],
    queryFn: adminApi.listDevices,
    refetchInterval: 5_000,
  });

  // マウントポイント一覧（どのデバイスにも紐付いていないマウントポイントも「マウントポイントから見る」で出すため）。
  const mountPointsQuery = useQuery({
    queryKey: ['mount-points'],
    queryFn: adminApi.listMountPoints,
    refetchInterval: 5_000,
  });

  const [tab, setTab] = useState<TabKey>('device');
  const [filter, setFilter] = useState('');
  const [pathTarget, setPathTarget] = useState<PathTarget | null>(null);

  const deletePath = useMutation({
    mutationFn: adminApi.deleteDeviceProjectMap,
    onSuccess: invalidate,
  });
  const setPathEnabled = useMutation({
    mutationFn: (vars: { mapId: number; enabled: boolean }) =>
      adminApi.setDeviceProjectMapEnabled(vars.mapId, vars.enabled),
    onSuccess: invalidate,
  });

  const devices = devicesQuery.data ?? [];
  const mountPoints = mountPointsQuery.data ?? [];

  const handleEdit = (p: DeviceProjectMapEntry) =>
    setPathTarget({ editing: p, fixedDevice: null });
  const handleDelete = (p: DeviceProjectMapEntry, dName: string) => {
    if (
      confirm(
        `マウントを削除しますか？\n${dName} — ${p.mountPoint.name}（${p.mountPoint.path}）`,
      )
    ) {
      deletePath.mutate(p.id);
    }
  };
  const handleToggle = (p: DeviceProjectMapEntry) =>
    setPathEnabled.mutate({ mapId: p.id, enabled: !p.enabled });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="k-card">
        <div style={{ marginBottom: 10 }}>
          <h2 style={{ margin: 0 }}>デバイスマウント</h2>
          <p className="k-muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
            各デバイスに、どのマウントポイント（論理名）を載せるかの登録・変更・削除。{' '}
            <strong>デバイスから見る</strong>（デバイスごと）と{' '}
            <strong>マウントポイントから見る</strong>（同じマウントポイントをデバイス横断で見る）をタブで切り替えられます。
            デバイス本体の登録・削除は <Link to="/settings/devices">デバイス画面</Link> で行います。
          </p>
        </div>

        {/* タブ＋絞り込み */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 14 }}>
          <TabButton active={tab === 'device'} onClick={() => setTab('device')}>
            🖥️ デバイスから見る
          </TabButton>
          <TabButton active={tab === 'path'} onClick={() => setTab('path')}>
            📌 マウントポイントから見る
          </TabButton>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="絞り込み…（マウントポイント・パス・用途メモ・デバイス）"
            style={{ marginLeft: 'auto', flex: '0 1 300px', padding: '6px 10px', fontSize: 13 }}
          />
        </div>

        {devicesQuery.isLoading && <p>読み込み中…</p>}
        {devicesQuery.error && (
          <p className="k-error">取得に失敗しました: {(devicesQuery.error as Error).message}</p>
        )}
        {deletePath.error && (
          <p className="k-error">マウント削除に失敗しました: {(deletePath.error as Error).message}</p>
        )}
        {setPathEnabled.error && (
          <p className="k-error">
            有効/無効の切り替えに失敗しました: {(setPathEnabled.error as Error).message}
          </p>
        )}

        {devicesQuery.data &&
          (tab === 'device' ? (
            <DeviceCentricView
              devices={devices}
              filter={filter}
              onAddPath={(d) => setPathTarget({ editing: null, fixedDevice: d })}
              onEditPath={handleEdit}
              onDeletePath={handleDelete}
              onToggleEnabled={handleToggle}
            />
          ) : (
            <PathCentricView
              devices={devices}
              mountPoints={mountPoints}
              filter={filter}
              onAddToPath={(mountPointId) =>
                setPathTarget({
                  editing: null,
                  fixedDevice: null,
                  presetMountPointId: mountPointId,
                })
              }
              onEditPath={handleEdit}
              onDeletePath={handleDelete}
              onToggleEnabled={handleToggle}
            />
          ))}
      </section>

      {pathTarget && (
        <PathDialog
          devices={devices}
          editing={pathTarget.editing}
          fixedDevice={pathTarget.fixedDevice}
          presetMountPointId={pathTarget.presetMountPointId}
          onClose={() => setPathTarget(null)}
          onSaved={() => {
            setPathTarget(null);
            invalidate();
          }}
        />
      )}
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '7px 14px',
        borderRadius: 8,
        fontSize: 13.5,
        fontWeight: active ? 700 : 500,
        border: '1px solid',
        borderColor: active ? '#1d4ed8' : '#e2e8f0',
        background: active ? '#eaf1ff' : '#fff',
        color: active ? '#1d4ed8' : '#475569',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// 絞り込み一致判定（パス・名前・用途・デバイス名）。
function matchEntry(d: DeviceListItem, p: DeviceProjectMapEntry, f: string): boolean {
  if (!f) return true;
  return (
    p.mountPoint.path.toLowerCase().includes(f) ||
    p.mountPoint.name.toLowerCase().includes(f) ||
    (p.purpose || '').toLowerCase().includes(f) ||
    d.name.toLowerCase().includes(f) ||
    (d.nickname || '').toLowerCase().includes(f)
  );
}

function OnlineDot({ online }: { online: boolean }) {
  return (
    <span
      title={online ? 'オンライン' : 'オフライン'}
      style={{
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: online ? '#16a34a' : '#cbd5e1',
        flexShrink: 0,
      }}
    />
  );
}

function EnabledBadge() {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 6px',
        borderRadius: 4,
        background: '#f1f5f9',
        color: '#64748b',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      無効
    </span>
  );
}

// どのデバイス／マウントにも紐付いていないことを明示するバッジ（琥珀系）。
function UnlinkedBadge({ label = '未紐付け' }: { label?: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 7px',
        borderRadius: 999,
        background: '#fef3c7',
        color: '#92400e',
        fontSize: 11,
        fontWeight: 600,
      }}
    >
      {label}
    </span>
  );
}

function PurposeCell({ p }: { p: DeviceProjectMapEntry }) {
  if (!p.purpose)
    return (
      <span className="k-muted" style={{ fontSize: 12 }}>
        —
      </span>
    );
  return <PurposeBadge name={p.purpose} />;
}

function CliCell({ p }: { p: DeviceProjectMapEntry }) {
  const mode = (p.cliOptions?.permissionMode as string) ?? 'dontAsk';
  const args = (p.cliOptions?.extraArgs as string[]) ?? [];
  const mcp = p.cliOptions?.mcpConfig as string | undefined;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ fontSize: 13 }}>{cliLabel(p.cliType)}</span>
      <span className="k-muted" style={{ fontSize: 11 }}>
        権限: {mode}
        {args.length > 0 && ` / args: ${args.length}件`}
        {mcp && ' / MCP設定あり'}
      </span>
    </div>
  );
}

function PathActions({
  p,
  dName,
  onEdit,
  onToggle,
  onDelete,
}: {
  p: DeviceProjectMapEntry;
  dName: string;
  onEdit: (p: DeviceProjectMapEntry) => void;
  onToggle: (p: DeviceProjectMapEntry) => void;
  onDelete: (p: DeviceProjectMapEntry, dName: string) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <button
        className="btn-ghost"
        type="button"
        style={{ padding: '2px 10px', fontSize: 12 }}
        onClick={() => onEdit(p)}
      >
        変更
      </button>
      <button
        className="btn-ghost"
        type="button"
        style={{ padding: '2px 10px', fontSize: 12 }}
        title={
          p.enabled
            ? 'このマウントを無効化（削除せず実行対象から外す。あとで有効化で即復活）'
            : 'このマウントを再度有効化する'
        }
        onClick={() => onToggle(p)}
      >
        {p.enabled ? '無効化' : '有効化'}
      </button>
      <button
        className="btn-danger"
        type="button"
        style={{ padding: '2px 10px', fontSize: 12 }}
        onClick={() => onDelete(p, dName)}
      >
        削除
      </button>
    </div>
  );
}

const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;

const groupCard = {
  border: '1px solid #e7ebf0',
  borderRadius: 10,
  overflow: 'hidden',
} as const;

const groupHeader = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  background: '#f8fafc',
  borderBottom: '1px solid #eef2f7',
} as const;

// ===== デバイスから見るタブ：デバイスごとに、載っているマウントを表示 =====
function DeviceCentricView({
  devices,
  filter,
  onAddPath,
  onEditPath,
  onDeletePath,
  onToggleEnabled,
}: {
  devices: DeviceListItem[];
  filter: string;
  onAddPath: (d: DeviceListItem) => void;
  onEditPath: (p: DeviceProjectMapEntry) => void;
  onDeletePath: (p: DeviceProjectMapEntry, dName: string) => void;
  onToggleEnabled: (p: DeviceProjectMapEntry) => void;
}) {
  const f = filter.trim().toLowerCase();

  if (devices.length === 0)
    return (
      <p className="k-muted">
        デバイスが未登録です。<Link to="/settings/devices">デバイス画面</Link>{' '}
        で登録してから、ここでマウントを追加してください。
      </p>
    );

  const visible = devices
    .map((d) => ({ device: d, paths: d.deviceProjectMap.filter((p) => matchEntry(d, p, f)) }))
    // 絞り込み中は、該当パスのないデバイスは隠す（絞り込みなしなら全デバイス表示）。
    .filter((g) => !f || g.paths.length > 0);

  if (visible.length === 0)
    return <p className="k-muted">絞り込みに一致するマウントがありません。</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {visible.map(({ device: d, paths }) => (
        <div key={d.id} style={groupCard}>
          <div style={groupHeader}>
            <OnlineDot online={d.online} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong>{deviceName(d)}</strong>
              {d.nickname && (
                <span className="k-muted" style={{ marginLeft: 6, fontSize: 12 }}>
                  （{d.name}）
                </span>
              )}
              <span className="k-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                {placementLabel(d.placementType)}
              </span>
              {paths.length === 0 && (
                <span style={{ marginLeft: 8 }}>
                  <UnlinkedBadge label="マウント未登録" />
                </span>
              )}
            </div>
            <button
              type="button"
              style={{ whiteSpace: 'nowrap', padding: '4px 12px', fontSize: 13 }}
              onClick={() => onAddPath(d)}
            >
              ＋ マウントを追加
            </button>
          </div>

          <div style={{ paddingLeft: 24 }}>
          {paths.length === 0 ? (
            <p className="k-muted" style={{ margin: 0, padding: '12px 14px', fontSize: 13 }}>
              マウント未登録 —「＋ マウントを追加」から登録してください。
            </p>
          ) : (
            <table className="k-table" style={{ margin: 0 }}>
              <thead>
                <tr>
                  <th>マウントポイント</th>
                  <th>用途メモ</th>
                  <th>CLI</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {paths.map((p) => (
                  <tr key={p.id} style={p.enabled ? undefined : { opacity: 0.55 }}>
                    <td style={td}>
                      <div
                        style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}
                      >
                        {p.mountPoint.name}
                        {!p.enabled && <EnabledBadge />}
                      </div>
                      <code style={{ fontSize: 12, color: '#64748b' }}>{p.mountPoint.path}</code>
                    </td>
                    <td style={td}>
                      <PurposeCell p={p} />
                    </td>
                    <td style={td}>
                      <CliCell p={p} />
                    </td>
                    <td style={td}>
                      <PathActions
                        p={p}
                        dName={deviceName(d)}
                        onEdit={onEditPath}
                        onToggle={onToggleEnabled}
                        onDelete={onDeletePath}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ===== マウントポイントから見るタブ：同じ MountPoint を束ね、デバイスを横断表示 =====
function PathCentricView({
  devices,
  mountPoints,
  filter,
  onAddToPath,
  onEditPath,
  onDeletePath,
  onToggleEnabled,
}: {
  devices: DeviceListItem[];
  mountPoints: MountPoint[];
  filter: string;
  onAddToPath: (mountPointId: number) => void;
  onEditPath: (p: DeviceProjectMapEntry) => void;
  onDeletePath: (p: DeviceProjectMapEntry, dName: string) => void;
  onToggleEnabled: (p: DeviceProjectMapEntry) => void;
}) {
  const f = filter.trim().toLowerCase();

  type Row = { device: DeviceListItem; p: DeviceProjectMapEntry };
  type Group = { mountPointId: number; name: string; path: string; rows: Row[] };
  const groups = new Map<number, Group>();
  // まずマウントポイント一覧から全グループを作る（紐付くデバイスが無いものも空グループとして出す）。
  for (const mp of mountPoints) {
    groups.set(mp.id, { mountPointId: mp.id, name: mp.name, path: mp.path, rows: [] });
  }
  // 各デバイスの紐付けを該当グループに足す。マウントポイント一覧に無いものが来ても拾えるよう、無ければ作る。
  for (const d of devices) {
    for (const p of d.deviceProjectMap) {
      const g = groups.get(p.mountPointId) ?? {
        mountPointId: p.mountPointId,
        name: p.mountPoint.name,
        path: p.mountPoint.path,
        rows: [],
      };
      g.rows.push({ device: d, p });
      groups.set(p.mountPointId, g);
    }
  }

  const hasAnyGroup = groups.size > 0;
  let groupList = [...groups.values()];

  // 絞り込み：マウントポイントの名前/パスが一致すればグループ丸ごと、そうでなければ行単位で絞る。
  // 名前/パス一致なら未紐付け（行ゼロ）でも残す。
  if (f) {
    const groupMatches = (g: Group) =>
      g.name.toLowerCase().includes(f) || g.path.toLowerCase().includes(f);
    groupList = groupList
      .map((g) =>
        groupMatches(g) ? g : { ...g, rows: g.rows.filter((r) => matchEntry(r.device, r.p, f)) },
      )
      .filter((g) => groupMatches(g) || g.rows.length > 0);
  }

  // 未紐付け（行ゼロ）を下に、紐付けありを上に。各ブロック内は名前順。
  groupList.sort((a, b) => {
    const ae = a.rows.length === 0 ? 1 : 0;
    const be = b.rows.length === 0 ? 1 : 0;
    return ae - be || a.name.localeCompare(b.name);
  });

  if (!hasAnyGroup)
    return (
      <p className="k-muted">
        マウントポイントが未登録です。
        <Link to="/settings/mount-points">マウントポイント画面</Link> で登録してください。
      </p>
    );
  if (groupList.length === 0)
    return <p className="k-muted">絞り込みに一致するマウントがありません。</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {groupList.map((g) => {
        return (
          <div key={g.mountPointId} style={groupCard}>
            <div style={groupHeader}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <strong style={{ fontSize: 13, color: '#0f172a' }}>{g.name}</strong>
                <code style={{ fontSize: 12, color: '#64748b', marginLeft: 8 }}>{g.path}</code>
                {g.rows.length === 0 ? (
                  <span style={{ marginLeft: 8 }}>
                    <UnlinkedBadge label="デバイス未紐付け" />
                  </span>
                ) : (
                  <span className="k-muted" style={{ marginLeft: 8, fontSize: 12 }}>
                    {g.rows.length} デバイス
                  </span>
                )}
              </div>
              <button
                type="button"
                className="btn-ghost"
                style={{ whiteSpace: 'nowrap', padding: '4px 12px', fontSize: 13 }}
                title="同じマウントポイントを別のデバイスにも登録する"
                onClick={() => onAddToPath(g.mountPointId)}
              >
                ＋ このマウントを別デバイスに
              </button>
            </div>

            <div style={{ paddingLeft: 24 }}>
            {g.rows.length === 0 ? (
              <p className="k-muted" style={{ margin: 0, padding: '12px 14px', fontSize: 13 }}>
                このマウントポイントはどのデバイスにも紐付いていません。「＋ このマウントを別デバイスに」から登録してください。
              </p>
            ) : (
              <table className="k-table" style={{ margin: 0 }}>
                <thead>
                  <tr>
                    <th>デバイス</th>
                    <th>用途メモ</th>
                    <th>CLI</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map(({ device: d, p }) => (
                  <tr key={p.id} style={p.enabled ? undefined : { opacity: 0.55 }}>
                    <td style={td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <OnlineDot online={d.online} />
                        <span style={{ fontWeight: 600 }}>{deviceName(d)}</span>
                      </div>
                      <span className="k-muted" style={{ fontSize: 12 }}>
                        {placementLabel(d.placementType)}
                      </span>
                      {!p.enabled && (
                        <span style={{ marginLeft: 6 }}>
                          <EnabledBadge />
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <PurposeCell p={p} />
                    </td>
                    <td style={td}>
                      <CliCell p={p} />
                    </td>
                    <td style={td}>
                      <PathActions
                        p={p}
                        dName={deviceName(d)}
                        onEdit={onEditPath}
                        onToggle={onToggleEnabled}
                        onDelete={onDeletePath}
                      />
                    </td>
                  </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ===== マウント追加 / 変更ダイアログ =====
// editing あり：変更（デバイスは固定）。なし：追加。
// fixedDevice あり（デバイスから見るタブからの追加）：追加先デバイスを固定。
// presetMountPointId（マウントポイントから見るタブからの追加）：マウントポイントを初期選択し、追加先デバイスを選ばせる。
// 物理パスは MountPoint 側に持つので、ここでは MountPoint をドロップダウンで選ぶだけ。
function PathDialog({
  devices,
  editing,
  fixedDevice,
  presetMountPointId,
  onClose,
  onSaved,
}: {
  devices: DeviceListItem[];
  editing: DeviceProjectMapEntry | null;
  fixedDevice: DeviceListItem | null;
  presetMountPointId?: number;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editingDevice = editing ? devices.find((d) => d.id === editing.deviceId) ?? null : null;
  const lockedDevice = editingDevice ?? fixedDevice;

  // ピッカー用のマウントポイント一覧。
  const mountPointsQuery = useQuery({
    queryKey: ['mount-points'],
    queryFn: adminApi.listMountPoints,
  });
  const mountPoints = mountPointsQuery.data ?? [];

  // マウントポイントから見るタブ（presetMountPointId）/ 編集では、マウントポイントは確定済み → 固定表示。
  const mountPointLocked = editing != null || presetMountPointId != null;
  const lockedMountPoint =
    editing?.mountPoint ??
    (presetMountPointId != null
      ? mountPoints.find((mp) => mp.id === presetMountPointId) ?? null
      : null);
  // 二重登録防止：このマウントポイントを既に持つデバイスは選ばせない。
  const selectableDevices = devices.filter(
    (d) =>
      presetMountPointId == null ||
      !d.deviceProjectMap.some((p) => p.mountPointId === presetMountPointId),
  );
  // 二重登録防止：このデバイスに既に載っているマウントポイントは選ばせない。
  const selectableMountPoints = mountPoints.filter(
    (mp) => !lockedDevice?.deviceProjectMap.some((p) => p.mountPointId === mp.id),
  );

  // 用途マスタ（マスタ管理 > 用途）。有効なものだけをドロップダウンに出す。
  const purposesQuery = useQuery({
    queryKey: ['mount-purposes'],
    queryFn: adminApi.listMountPurposes,
  });
  const purposes = (purposesQuery.data ?? []).filter((p) => p.enabled);

  const [deviceId, setDeviceId] = useState<number | ''>(lockedDevice?.id ?? '');
  const [mountPointId, setMountPointId] = useState<number | ''>(
    editing?.mountPointId ?? presetMountPointId ?? '',
  );
  const [purpose, setPurpose] = useState(editing?.purpose ?? '');
  const [cliType, setCliType] = useState<CliType>(editing?.cliType ?? 'claude_code');
  const [extraArgs, setExtraArgs] = useState<string>(
    ((editing?.cliOptions?.extraArgs as string[]) ?? []).join('\n'),
  );
  const [permissionMode, setPermissionMode] = useState<string>(
    (editing?.cliOptions?.permissionMode as string) ?? 'dontAsk',
  );
  const [mcpConfig, setMcpConfig] = useState<string>(
    (editing?.cliOptions?.mcpConfig as string) ?? '',
  );

  const save = useMutation({
    mutationFn: () => {
      const use = purpose.trim() || null; // 空文字は未設定(null)で送る
      const cliOpts: Record<string, unknown> = {
        extraArgs: extraArgs.split('\n').map((s) => s.trim()).filter(Boolean),
        permissionMode: permissionMode || 'dontAsk',
      };
      if (mcpConfig.trim()) cliOpts.mcpConfig = mcpConfig.trim();
      if (editing) {
        return adminApi.updateDeviceProjectMap(editing.id, {
          mountPointId: Number(mountPointId),
          purpose: use,
          cliType,
          cliOptions: cliOpts,
        });
      }
      return adminApi.createDeviceProjectMap({
        deviceId: Number(deviceId),
        mountPointId: Number(mountPointId),
        purpose: use,
        cliType,
        cliOptions: cliOpts,
      });
    },
    onSuccess: onSaved,
  });

  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;

  const canSubmit =
    mountPointId !== '' && (editing != null || lockedDevice != null || deviceId !== '');

  const title = editing
    ? `マウントを変更 — ${lockedDevice ? deviceName(lockedDevice) : ''}`
    : fixedDevice
      ? `マウントを追加 — ${deviceName(fixedDevice)}`
      : 'このマウントを別デバイスに追加';

  return (
    <Modal title={title} onClose={onClose} width={520}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          save.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        {/* デバイス：変更/固定なら表示のみ、それ以外は選択 */}
        <div style={fieldWrap}>
          <label style={labelStyle}>デバイス</label>
          {lockedDevice ? (
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {deviceName(lockedDevice)}
              {lockedDevice.nickname && (
                <span className="k-muted" style={{ marginLeft: 6, fontSize: 12, fontWeight: 400 }}>
                  （{lockedDevice.name}）
                </span>
              )}
            </div>
          ) : (
            <>
              <select
                value={deviceId}
                onChange={(e) => setDeviceId(e.target.value ? Number(e.target.value) : '')}
                required
                autoFocus
              >
                <option value="">追加先のデバイスを選択…</option>
                {selectableDevices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {deviceName(d)} — {placementLabel(d.placementType)}
                  </option>
                ))}
              </select>
              {selectableDevices.length === 0 && (
                <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
                  このマウントポイントは全デバイスに登録済みです。
                </p>
              )}
            </>
          )}
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>マウントポイント（作業 DIR）</label>
          {mountPointLocked ? (
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {lockedMountPoint ? (
                <>
                  {lockedMountPoint.name}
                  <code
                    style={{ fontSize: 12, color: '#64748b', marginLeft: 8, fontWeight: 400 }}
                  >
                    {lockedMountPoint.path}
                  </code>
                </>
              ) : (
                <span className="k-muted" style={{ fontSize: 13, fontWeight: 400 }}>
                  読み込み中…
                </span>
              )}
            </div>
          ) : (
            <>
              <select
                value={mountPointId}
                onChange={(e) => setMountPointId(e.target.value ? Number(e.target.value) : '')}
                required
                autoFocus={!!lockedDevice}
              >
                <option value="">マウントポイントを選択…</option>
                {selectableMountPoints.map((mp) => (
                  <option key={mp.id} value={mp.id}>
                    {mp.name} — {mp.path}
                  </option>
                ))}
              </select>
              {mountPointsQuery.isLoading && (
                <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
                  マウントポイントを読み込み中…
                </p>
              )}
              {!mountPointsQuery.isLoading && selectableMountPoints.length === 0 && (
                <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
                  {mountPoints.length === 0 ? (
                    <>
                      マウントポイントが未登録です。
                      <Link to="/settings/mount-points">マウントポイント画面</Link> で登録してください。
                    </>
                  ) : (
                    'このデバイスには全マウントポイントが登録済みです。'
                  )}
                </p>
              )}
              <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
                物理パスはマウントポイント側で管理します。ここでは載せるマウントポイントを選ぶだけです。
              </p>
            </>
          )}
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>用途メモ（任意）</label>
          <select value={purpose} onChange={(e) => setPurpose(e.target.value)}>
            <option value="">（未設定）</option>
            {/* 既存値がマスタ（有効分）に無い場合（無効化済み・旧自由入力値）も選択状態を保てるよう先頭に出す。 */}
            {purpose && !purposes.some((p) => p.label === purpose) && (
              <option value={purpose}>{purpose}（マスタ外）</option>
            )}
            {purposes.map((p) => (
              <option key={p.id} value={p.label}>
                {p.label}
              </option>
            ))}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            このマウントを何用に使うかの覚え書き（あくまで参考）。選択肢は「マスタ管理 &gt; 用途メモ」で管理します。同じマウントポイントでもデバイスごとに役割が違うとき、横断で見る目印になります。
          </p>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>CLI</label>
          <select value={cliType} onChange={(e) => setCliType(e.target.value as CliType)}>
            {cliOptions.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>権限モード（permissionMode）</label>
          <select value={permissionMode} onChange={(e) => setPermissionMode(e.target.value)}>
            <option value="dontAsk">dontAsk — 許可確認をスキップ（推奨）</option>
            <option value="default">default — 都度確認する</option>
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            dontAsk: CLI が自動的にすべての操作を実行します（--dangerously-skip-permissions 相当）。
          </p>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>追加 CLI 引数（extraArgs）</label>
          <textarea
            value={extraArgs}
            onChange={(e) => setExtraArgs(e.target.value)}
            rows={3}
            placeholder={'--no-auto-updates\n--model claude-opus-4-8'}
            style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
          />
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            1行1引数。空行は無視されます。
          </p>
        </div>

        <div style={fieldWrap}>
          <label style={labelStyle}>MCP 設定ファイルパス（mcpConfig・任意）</label>
          <input
            type="text"
            value={mcpConfig}
            onChange={(e) => setMcpConfig(e.target.value)}
            placeholder="/path/to/.mcp.json"
            style={{ fontFamily: 'monospace', fontSize: 12 }}
          />
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            空欄の場合は各作業DirのデフォルトMCP設定を使用します。
          </p>
        </div>

        {save.error && <p className="k-error">{(save.error as Error).message}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={save.isPending || !canSubmit}>
            {editing ? '変更を保存' : '追加'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
